import React, { useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Popper from "@mui/material/Popper";
import Grow from "@mui/material/Grow";
import Paper from "@mui/material/Paper";
import MenuList from "@mui/material/MenuList";
import MenuItem from "@mui/material/MenuItem";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import GradingIcon from "@mui/icons-material/Grading";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import LoadingButton from "_shared/_views/loading/loading-button";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import WarningIcon from "@mui/icons-material/Warning";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";

const SplitButtonContainer = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  minWidth: theme.spacing(42),
  position: 'relative',
  width: '100%'
}));
const MenuPaper = styled(Paper)(({ theme }) => ({
  border: `1px solid ${theme.palette.primary.main}`,
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0,
  borderTopWidth: 0,
}));

type Props = {
  className?: string;
  hasPendingChanges: boolean;
  hasPendingComments: boolean;
  disabled?: boolean;
  disableDropdown?: boolean;
  finalized?: boolean;
  loading?: boolean;
  onFinalize: () => void;
  onSubmitAllAndFinalize: () => void;
  onSubmitChangesAndComments: () => void;
  onSubmitCommentsOnly: () => void;
};

export default function SubmitReviewButton({
  className,
  hasPendingChanges,
  hasPendingComments,
  disabled,
  disableDropdown,
  finalized,
  loading,
  onFinalize,
  onSubmitAllAndFinalize,
  onSubmitChangesAndComments,
  onSubmitCommentsOnly,
}: Readonly<Props>) {
  const anchorRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const confirm = useConfirmDialog();

  React.useEffect(() => {
    setAnchorEl(anchorRef.current);
  }, []);

  const handleFinalize = async (submitPendingItems = false) => {
    let title = '';
    let message = '';

    if (submitPendingItems && (hasPendingChanges || hasPendingComments)) {
      title = "Submit All & Finalize?";
      message = "Are you sure you want to submit all pending changes and comments, then finish reviewing this proposal?";
    } else {
      title = "Finalize Review?";
      message = "Are you sure you want to finish reviewing this proposal? No additional changes or comments will be accepted.";
    }

    const response = await confirm({
      title,
      message,
      okButtonText: "Yes",
    });

    if (response === ConfirmResponse.Cancel) return;

    if (submitPendingItems) {
      onSubmitAllAndFinalize();
      return;
    }

    onFinalize();
  };

  const options = [
    {
      label: finalized ? "Review Finalized" : "Submit All & Finalize",
      action: () => handleFinalize(true),
      disabled: finalized,
      icon: (hasPendingChanges || hasPendingComments) ? (
        <Tooltip title="You have un-shared changes and/or comments">
          <WarningIcon color="warning" />
        </Tooltip>
      ) : undefined,
    },
    {
      label: "Submit Comments",
      action: () => onSubmitCommentsOnly(),
      disabled: !hasPendingComments,
      icon: hasPendingComments ? (
        <Tooltip title="You have un-shared changes & comments">
          <WarningIcon color="warning" />
        </Tooltip>
      ) : undefined,
    },
    {
      label: hasPendingComments ? "Submit Changes & Comments" : "Submit Changes",
      action: () => onSubmitChangesAndComments(),
      disabled: !hasPendingChanges
    },
  ];

  const isButtonDisabled = Boolean(disabled || (!hasPendingComments && !hasPendingChanges && finalized));
  const enabledOptions = options.filter(o => !o.disabled).length;

  const handlePrimaryClick = async () => {
    if (loading) return;

    if (finalized) {
      if (hasPendingChanges) {
        onSubmitChangesAndComments();
        return;
      }
      if (hasPendingComments) {
        onSubmitCommentsOnly();
        return;
      }
      return;
    }

    await handleFinalize(hasPendingChanges || hasPendingComments);
  };

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };

  const handleMenuItemClick = (index: number) => {
    setOpen(false);
    (options[index]).action();
  };

  const handleClose = (event: Event) => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) return;
    setOpen(false);
  };

  const getButtonLabel = () => {
    if (!hasPendingComments && !hasPendingChanges && finalized) {
      return "Review Finalized";
    }
    if (hasPendingComments && !hasPendingChanges && finalized) {
      return "Submit Comments";
    }
    if (!hasPendingComments && hasPendingChanges && finalized) {
      return "Submit Changes";
    }
    if (hasPendingComments && hasPendingChanges && finalized) {
      return "Submit Changes & Comments";
    }

    return "Submit Review";
  };

  return (
    <SplitButtonContainer className={className}>
      <ButtonGroup
        disableElevation={open}
        variant="contained"
        ref={anchorRef}
        aria-label="submit review split button"
        sx={{ width: '100%' }}
      >
        <LoadingButton
          onClick={handlePrimaryClick}
          startIcon={<GradingIcon />}
          sx={{
            borderBottomLeftRadius: open ? 0 : undefined,
            borderBottomRightRadius: open ? 0 : undefined,
            width: '100%'
          }}
          loading={!!loading}
          disabled={isButtonDisabled}
        >
          {getButtonLabel()}
        </LoadingButton>
        {!disableDropdown && enabledOptions > 1 && (
          <Button
            size="small"
            aria-controls={open ? "submit-review-menu" : undefined}
            aria-expanded={open ? "true" : undefined}
            aria-haspopup="menu"
            disabled={isButtonDisabled || !!loading}
            sx={{ borderBottomRightRadius: open ? 0 : undefined }}
            onClick={handleToggle}
          >
            <ArrowDropDownIcon />
          </Button>
        )}
      </ButtonGroup>

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-end"
        transition
        disablePortal
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, -1],
            },
          },
        ]}
        sx={{ width: '100%', zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: 'top' }}>
            <MenuPaper elevation={0}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="submit-review-menu">
                  {options.map((option, index) => (
                    <MenuItem
                      key={option.label}
                      disabled={option.disabled}
                      sx={{ justifyContent: 'center', paddingRight: 6 }}
                      onClick={() => handleMenuItemClick(index)}
                    >
                      <ListItemIcon>
                        {option.icon}
                      </ListItemIcon>
                      <ListItemText>
                        {option.label}
                      </ListItemText>
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </MenuPaper>
          </Grow>
        )}
      </Popper>
    </SplitButtonContainer>
  );
}
